import {
  AFFILIATE_EARNINGS_PAYMENT_STATES,
  AFFILIATE_EARNINGS_RANGE_IDS,
  AFFILIATE_EARNINGS_SUPPORT,
  affiliateEarningsRangeIssue,
  withAffiliateEarningsRange,
} from '@wlb/shared/api/affiliate/affiliateEarnings';
import type {
  AffiliateEarningsFilter,
  AffiliateEarningsPaymentState,
  AffiliateEarningsRangeId,
  AffiliateEarningsSummary,
} from '@wlb/shared/api/affiliate/affiliateTypes';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { FormError } from '../../components/ui/FormError';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatAffiliateCount, formatAffiliateCurrency } from './affiliateFormat';
import './affiliatePortal.css';

interface AffiliateEarningsPanelProps {
  filter: AffiliateEarningsFilter;
  onFilterChange: (next: AffiliateEarningsFilter) => void;
  summary: AffiliateEarningsSummary | null;
  loading: boolean;
}

const PAYMENT_CONTROLS_ENABLED = AFFILIATE_EARNINGS_SUPPORT.paymentState;

const PAYMENT_NOTE_ID = 'affiliate-earnings-payment-unsupported';
/** One message for a range spanning two inputs, so both point at the same id. */
const RANGE_ERROR_ID = 'affiliate-earnings-range-error';

/**
 * Earnings filters and totals for the affiliate portal.
 *
 * The period is sent to `GetAffiliateReferralCodes`, which takes a date
 * interval and reports the paid/unpaid breakdown. The payment-status control
 * stays disabled: no affiliate read filters rows by paid or due, so an enabled
 * one would promise a narrowing the API cannot do.
 *
 * Signups and Eligible are two separate tiles because they are two different
 * numbers now: a signup lands pending and only earns commission once the
 * referred member clears the qualification rule. `totalEarnings` prices the
 * eligible referrals alone, so it reads lower than it used to — the pending
 * remainder is shown beside it rather than left to be discovered as a drop.
 */
export function AffiliateEarningsPanel({
  filter,
  onFilterChange,
  summary,
  loading,
}: AffiliateEarningsPanelProps) {
  const { t } = useTranslation();

  const rangeIssue = affiliateEarningsRangeIssue(filter);
  const rangeError =
    rangeIssue === 'none'
      ? ''
      : t(
          rangeIssue === 'inverted'
            ? 'affiliate.dashboard.earnings.rangeInvalid'
            : 'affiliate.dashboard.earnings.rangeIncomplete',
        );

  const money = (value: number | null): string =>
    value === null
      ? t('affiliate.dashboard.earnings.notAvailable')
      : formatAffiliateCurrency(value);

  const count = (value: number | null): string =>
    value === null
      ? t('affiliate.dashboard.earnings.notAvailable')
      : formatAffiliateCount(value);

  const tiles: readonly { key: string; label: string; value: string }[] = [
    {
      key: 'signups',
      label: t('affiliate.dashboard.earnings.signups'),
      value: formatAffiliateCount(summary?.totalSignups ?? 0),
    },
    {
      key: 'eligible',
      label: t('affiliate.dashboard.earnings.eligible'),
      value: count(summary?.eligibleCount ?? null),
    },
    {
      key: 'pending',
      label: t('affiliate.dashboard.earnings.pending'),
      value: count(summary?.pendingCount ?? null),
    },
    {
      key: 'totalEarned',
      label: t('affiliate.dashboard.earnings.totalEarned'),
      value: formatAffiliateCurrency(summary?.totalEarnings ?? 0),
    },
    {
      key: 'pendingEarnings',
      label: t('affiliate.dashboard.earnings.pendingEarnings'),
      value: money(summary?.pendingEarnings ?? null),
    },
    {
      key: 'paid',
      label: t('affiliate.dashboard.earnings.paid'),
      value: money(summary?.paidEarnings ?? null),
    },
    {
      key: 'due',
      label: t('affiliate.dashboard.earnings.due'),
      value: money(summary?.dueEarnings ?? null),
    },
    {
      key: 'unclaimed',
      label: t('affiliate.dashboard.earnings.unclaimed'),
      value: money(summary?.unclaimedEarnings ?? null),
    },
    {
      key: 'lifetimeEarned',
      label: t('affiliate.dashboard.earnings.lifetimeEarned'),
      value: money(summary?.lifetimeEarnings ?? null),
    },
  ];

  /**
   * The one-line reading of the two earnings tiles, so the lower Earned figure
   * explains itself on sight instead of looking like money that went missing.
   * Shown only once a pending amount was actually reported: with nothing
   * pending there is no drop to account for.
   */
  const pendingEarnings = summary?.pendingEarnings ?? null;
  const earnedSummary =
    pendingEarnings !== null && pendingEarnings > 0
      ? t('affiliate.dashboard.earnings.earnedSummary', {
          earned: formatAffiliateCurrency(summary?.totalEarnings ?? 0),
          pending: formatAffiliateCurrency(pendingEarnings),
        })
      : '';

  return (
    <section className="affiliate-earnings">
      <div className="affiliate-earnings__head">
        <h2 className="affiliate-dashboard__panel-title">
          {t('affiliate.dashboard.earnings.title')}
        </h2>
      </div>

      <fieldset className="affiliate-earnings__filters">
        <legend className="sr-only">{t('affiliate.dashboard.earnings.title')}</legend>

        <label className="auth-flow__field affiliate-earnings__field">
          <span>{t('affiliate.dashboard.earnings.rangeLabel')}</span>
          <select
            className="affiliate-auth__select"
            value={filter.rangeId}
            onChange={event =>
              onFilterChange(
                withAffiliateEarningsRange(
                  filter,
                  event.target.value as AffiliateEarningsRangeId,
                ),
              )
            }
          >
            {AFFILIATE_EARNINGS_RANGE_IDS.map(rangeId => (
              <option key={rangeId} value={rangeId}>
                {t(`affiliate.dashboard.earnings.ranges.${rangeId}`)}
              </option>
            ))}
          </select>
        </label>

        {filter.rangeId === 'custom' && (
          <>
            <label className="auth-flow__field affiliate-earnings__field">
              <span>{t('affiliate.dashboard.earnings.startDate')}</span>
              <input
                className={`auth-flow__input${
                  rangeError ? ' auth-flow__input--error' : ''
                }`}
                type="date"
                value={filter.startDate}
                max={filter.endDate || undefined}
                aria-invalid={rangeError ? true : undefined}
                aria-describedby={rangeError ? RANGE_ERROR_ID : undefined}
                onChange={event =>
                  onFilterChange({ ...filter, startDate: event.target.value })
                }
              />
            </label>
            <label className="auth-flow__field affiliate-earnings__field">
              <span>{t('affiliate.dashboard.earnings.endDate')}</span>
              <input
                className={`auth-flow__input${
                  rangeError ? ' auth-flow__input--error' : ''
                }`}
                type="date"
                value={filter.endDate}
                min={filter.startDate || undefined}
                aria-invalid={rangeError ? true : undefined}
                aria-describedby={rangeError ? RANGE_ERROR_ID : undefined}
                onChange={event =>
                  onFilterChange({ ...filter, endDate: event.target.value })
                }
              />
            </label>
          </>
        )}

        <label className="auth-flow__field affiliate-earnings__field">
          <span>{t('affiliate.dashboard.earnings.paymentStateLabel')}</span>
          <select
            className="affiliate-auth__select"
            value={filter.paymentState}
            disabled={!PAYMENT_CONTROLS_ENABLED}
            aria-describedby={PAYMENT_CONTROLS_ENABLED ? undefined : PAYMENT_NOTE_ID}
            onChange={event =>
              onFilterChange({
                ...filter,
                paymentState: event.target.value as AffiliateEarningsPaymentState,
              })
            }
          >
            {AFFILIATE_EARNINGS_PAYMENT_STATES.map(state => (
              <option key={state} value={state}>
                {t(`affiliate.dashboard.earnings.paymentStates.${state}`)}
              </option>
            ))}
          </select>
        </label>
      </fieldset>

      {/* The error belongs to the pair of date inputs, not to whichever one was
          typed in last, so both describe themselves with this single banner
          below the filter row rather than each repeating the same sentence. */}
      <FormError variant="banner" message={rangeError} id={RANGE_ERROR_ID} />

      {!PAYMENT_CONTROLS_ENABLED && (
        <p className="affiliate-earnings__note" id={PAYMENT_NOTE_ID}>
          {t('affiliate.dashboard.earnings.unsupportedPayment')}
        </p>
      )}

      <dl className="affiliate-earnings__tiles">
        {tiles.map(tile => (
          <div className="affiliate-earnings__tile" key={tile.key}>
            <dt className="affiliate-earnings__tile-label">{tile.label}</dt>
            <dd className="affiliate-earnings__tile-value">
              {loading ? (
                <Skeleton className="affiliate-earnings__tile-skeleton" />
              ) : (
                tile.value
              )}
            </dd>
          </div>
        ))}
      </dl>

      {!loading && earnedSummary ? (
        <p className="affiliate-earnings__summary">{earnedSummary}</p>
      ) : null}

      <p className="affiliate-earnings__note">
        {t('affiliate.dashboard.earnings.eligibleOnlyNote')}
      </p>

      <p className="affiliate-earnings__note">
        {t('affiliate.dashboard.earnings.balanceNote')}
      </p>
    </section>
  );
}
